import { useEffect } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { useForm } from "react-hook-form";
import { Link } from 'react-router-dom';
import FormContainer from '../../../components/FormContainer';
import SmashSpinner from '../../../components/SmashSpinner/SmashSpinner';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux/hooks';
import { validateEmail } from '../../../shared/validation/email';
import { validatePasswordLength } from '../../../shared/validation/length';
import { reset, selectedUser, signup } from '../authSlice';

const SignupForm = () => {
    const dispatch = useAppDispatch();

    type FormValues = {
        username: string;
        password: string;
        terms: boolean;
    };

    const { handleSubmit, setError, formState: { errors }, register } = useForm<FormValues>({
        mode: "onTouched",
        reValidateMode: "onChange"
    });

    const submitHandler = (values: FormValues) => {
        dispatch(signup(values));
    }

    const { isError, isSuccess, error, isAuthenticated, identity, signup: signupState } = useAppSelector(selectedUser);

    useEffect(() => {
        if (isError && error) {
            setError("username", { type: "custom", message: error.message });
            dispatch(reset());
        }
        if (isSuccess && identity) {
            dispatch(reset());
        }
        if (!isAuthenticated) {
            return;
        }
    }, [isSuccess, isAuthenticated, isError, error, identity, signupState, setError, dispatch]);

    if (signupState?.isLoading)
        return (
            <div className="h-100 d-flex d-column align-items-center">
                <SmashSpinner />
            </div>
        );

    return (
        <FormContainer>
            <Helmet>
                <title>Smash API - Sign Up</title>
                <meta name="description" content="Sign up for a Smash API account to start using our Smash API services." />
            </Helmet>
            <h5 className="my-5"><strong>Sign up to get started</strong></h5>
            <Form noValidate onSubmit={handleSubmit(submitHandler)}>
                <Form.Group className="mb-3" controlId="email">
                    <Form.Label><strong>Email</strong></Form.Label>
                    <Form.Control
                        {...register('username', { required: true, validate: validateEmail })}
                        type="email" placeholder=""
                        isInvalid={!!errors.username} />
                    {errors.username?.type === "validate" && (
                        <Form.Control.Feedback type="invalid">
                            Provide a valid email
                        </Form.Control.Feedback>)}
                    {errors.username?.type === "required" && (
                        <Form.Control.Feedback type="invalid">
                            An email is required
                        </Form.Control.Feedback>)}
                    {errors.username?.type === "custom" && (
                        <Form.Control.Feedback type="invalid">
                            Email is already taken
                        </Form.Control.Feedback>)}
                </Form.Group>
                <Form.Group className="mb-3" controlId="password">
                    <Form.Label><strong>Password</strong></Form.Label>
                    <Form.Control
                        {...register('password', { required: true, validate: validatePasswordLength })}
                        type="password" placeholder=""
                        isInvalid={!!errors.password}
                    />
                    {errors.password?.type === "validate" && (
                        <Form.Control.Feedback type="invalid">
                            Your password must contain at least 4 char
                        </Form.Control.Feedback>)}
                    {errors.password?.type === "required" && (
                        <Form.Control.Feedback type="invalid">
                            The password is required
                        </Form.Control.Feedback>)}
                </Form.Group>
                <Form.Group className="mb-3" controlId="terms">
                    <Form.Check.Input
                        type="checkbox"
                        isInvalid={!!errors.terms}
                        {...register('terms', { required: true })} />
                    <Form.Check.Label className="mx-1">By signing up, you agree with our <Link className="link-secondary fw-bolder" to="https://api.fromsmash.com/general-terms-of-sale" target="_blank">Terms of Service</Link>  </Form.Check.Label>
                    {errors.terms?.type === "required" && (
                        <Form.Control.Feedback type="invalid">
                            You must accept our Terms of service
                        </Form.Control.Feedback>)}
                </Form.Group>
                <Row >
                    <Col className="d-flex justify-content-center my-2">
                        {isError && errors.username && (
                            <div className="text-danger text-end">
                                {errors.username.message}
                            </div>)}
                        {signupState.isError && signupState.error?.message && (
                            <div className="text-danger text-end">
                                {signupState.error?.message}
                            </div>)}
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <div className="d-grid gap-2">
                            <Button variant="primary" type="submit">
                                Sign up
                            </Button>
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <div className="my-3 fs-8 d-flex justify-content-center align-items-center">
                            <p>Already have an account? <Link className="link-primary fw-bolder" to="/login">Log in</Link></p>
                        </div>
                    </Col>
                </Row>
            </Form>
        </FormContainer>
    )
}

export default SignupForm